import { useTranslation } from 'react-i18next';
import { AnimatedSection, SEO } from '~/components/common';
import { signUpURL } from '~/utils/constants/env';

const Features: React.FC = () => {
  //#region Declare Hook
  const { t } = useTranslation();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  const featuresList = [
    {
      id: 'aiChat',
      icon: '💬',
      gradient: 'from-lumi-500 to-lumi-cyan-500',
      bgGradient: 'from-lumi-900/50 to-lumi-dark-200',
      reverse: false,
    },
    {
      id: 'goals',
      icon: '🎯',
      gradient: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-900/30 to-lumi-dark-200',
      reverse: true,
    },
    {
      id: 'journal',
      icon: '📓',
      gradient: 'from-amber-500 to-orange-500',
      bgGradient: 'from-amber-900/30 to-lumi-dark-200',
      reverse: false,
    },
    {
      id: 'habits',
      icon: '🔁',
      gradient: 'from-emerald-500 to-teal-500',
      bgGradient: 'from-emerald-900/30 to-lumi-dark-200',
      reverse: true,
    },
    {
      id: 'dashboard',
      icon: '📊',
      gradient: 'from-blue-500 to-indigo-500',
      bgGradient: 'from-blue-900/30 to-lumi-dark-200',
      reverse: false,
    },
  ];

  return (
    <div className='min-h-screen bg-lumi-dark-300 text-white'>
      <SEO type='features' />
      {/* Hero */}
      <section className='pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden'>
        <div className='absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-lumi-700/15 blur-[100px] pointer-events-none' />
        <AnimatedSection animation='slide-up' className='relative z-10 max-w-3xl mx-auto'>
          <h1 className='text-4xl sm:text-5xl font-bold text-white mb-6'>{t('features.hero.title')}</h1>
          <p className='text-white/60 text-lg leading-relaxed'>{t('features.hero.subtitle')}</p>
        </AnimatedSection>
      </section>

      {/* Features Detail */}
      {featuresList.map((feature, index) => (
        <section key={feature.id} className='py-20 px-4 sm:px-6 lg:px-8'>
          <div className='max-w-6xl mx-auto'>
            <div
              className={`flex flex-col ${feature.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-16`}
            >
              {/* Visual */}
              <AnimatedSection
                animation={feature.reverse ? 'slide-left' : 'slide-right'}
                delay={100}
                className='flex-1 w-full flex items-center justify-center'
              >
                <div
                  className={`w-full max-w-none lg:max-w-md h-72 rounded-3xl bg-gradient-to-br ${feature.bgGradient} border border-white/10 flex items-center justify-center relative overflow-hidden`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-10`} />
                  <div
                    className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-5xl shadow-2xl`}
                  >
                    {feature.icon}
                  </div>
                </div>
              </AnimatedSection>

              {/* Content */}
              <AnimatedSection
                animation={feature.reverse ? 'slide-right' : 'slide-left'}
                delay={200}
                className='flex-1'
              >
                <p
                  className={`text-sm font-semibold bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent uppercase tracking-wider mb-3`}
                >
                  {t(`features.${feature.id}.subtitle`)}
                </p>
                <h2 className='text-3xl font-bold text-white mb-4'>{t(`features.${feature.id}.title`)}</h2>
                <p className='text-white/60 text-lg leading-relaxed mb-8'>{t(`features.${feature.id}.description`)}</p>
                <ul className='space-y-3'>
                  {[1, 2, 3, 4].map((num) => (
                    <li key={num} className='flex items-start gap-3'>
                      <div
                        className={`w-5 h-5 rounded-full bg-gradient-to-br ${feature.gradient} flex items-center justify-center flex-shrink-0 mt-0.5`}
                      >
                        <svg className='w-3 h-3 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={3} d='M5 13l4 4L19 7' />
                        </svg>
                      </div>
                      <span className='text-white/70 text-sm leading-relaxed'>
                        {t(`features.${feature.id}.feature${num}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </AnimatedSection>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className='py-24 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-3xl mx-auto text-center'>
          <AnimatedSection
            animation='scale-in'
            className='p-12 rounded-3xl bg-gradient-to-br from-lumi-800/50 to-lumi-dark-200 border border-lumi-500/30'
          >
            <h2 className='text-3xl font-bold text-white mb-4'>{t('features.cta.title')}</h2>
            <p className='text-white/50 text-lg mb-8'>{t('features.cta.subtitle')}</p>
            <a
              href={signUpURL}
              className='inline-flex items-center px-8 py-4 rounded-xl bg-lumi-600 hover:bg-lumi-500 text-white font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-lumi-600/40 hover:-translate-y-0.5'
            >
              {t('common.getStarted')}
            </a>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Features;
